import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { CellValueChangedEvent, ColDef } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";

import "../../../assets/css/AGGrid-custom.css";
import { CustomButton } from "../../../components/common/customButton";
import { CustomIconButton } from "../../../components/common/customIconButton";
import { ErrorDialog } from "../../../components/common/errorDialog";
import pencilIcon from "@/assets/svg/edit.svg";
import { useCurrentUser } from "@/context/CurrentUserContext";
import { useReferenceLists } from "../../../context/GlobalGetLists";
import { useErrorDialog } from "../../../hooks/useErrorDialog";
import { buildPipelineColumnDefs } from "../pipelineColumnDefs";
import { apiGet, apiPut } from "../../../services/genesisApi/httpsMethods";
import { exportKilledDealsToExcel } from "../../../services/summaryExportService";
import type { Deal } from "../../../types/deal";
import { prepareDealPutPayload } from "../../../utils/dealPutHelpers";
import { isGridCellValueEqual } from "../../../utils/compareData";
import {
  hasKillReason,
  hasKilledDealRowEdits,
  isKilledStatus,
} from "../../../utils/dealStatusHelpers";
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

ModuleRegistry.registerModules([AllCommunityModule]);

const KILLED_DEALS_COLUMNS = new Set([
  "deal_name",
  "status",
  "kill_reason",
  "countries",
  "loan_amount_fully_funded",
]);

const isAccordionLike = (deal: Deal): boolean => {
  if (!deal || typeof deal !== "object") return false;
  return (
    typeof (deal as any).accordion_id === "number" ||
    typeof (deal as any).accordion_name === "string"
  );
};

export function KilledDealsTab() {
  const { canEdit } = useCurrentUser();
  const [isLoading, setIsLoading] = useState(false);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [initialDeals, setInitialDeals] = useState<Deal[]>([]);
  const [missingOnly, setMissingOnly] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const referenceLists = useReferenceLists();
  const {
    killReasons,
    condensedSponsors,
    sources,
    countries,
    sectors,
    status,
    users,
    loanTypes,
    riskClasses,
    securityTypes,
    transactionTypes,
    currencies,
    capitalSources,
  } = referenceLists;
  const reasons = killReasons ?? [];
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  const loadDeals = useCallback(() => {
    setIsLoading(true);
    return apiGet<Deal[]>("deals?active_only=false")
      .then((response) => {
        if (response.success && response.data) {
          setDeals(response.data);
          setInitialDeals(structuredClone(response.data));
        } else {
          showErrorFromResponse(response);
        }
      })
      .catch(() => {
        showErrorFromResponse({
          success: false,
          status: 500,
          message: "Failed to fetch deals. Please try again.",
        });
      })
      .finally(() => setIsLoading(false));
  }, [showErrorFromResponse]);

  useEffect(() => {
    void loadDeals();
    // Fetch once on mount; showErrorFromResponse is not stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const killedDeals = useMemo(() => {
    return deals.filter((d) => {
      if (isAccordionLike(d)) return false;
      return isKilledStatus(d?.status?.status);
    });
  }, [deals]);

  const initialKilledDeals = useMemo(() => {
    return initialDeals.filter((d) => {
      if (isAccordionLike(d)) return false;
      return isKilledStatus(d?.status?.status);
    });
  }, [initialDeals]);

  const missingKillReasonCount = useMemo(
    () => initialKilledDeals.filter((d) => !hasKillReason(d)).length,
    [initialKilledDeals],
  );

  const filteredDeals = useMemo(() => {
    if (!missingOnly) return killedDeals;
    return killedDeals.filter((d) => {
      if (editMode) {
        const initial = initialDeals.find((row) => row.id === d.id);
        return initial != null && !hasKillReason(initial);
      }
      return !hasKillReason(d);
    });
  }, [killedDeals, missingOnly, editMode, initialDeals]);

  const dirtyGrid = useMemo(
    () =>
      killedDeals.some((deal) => {
        const initial = initialDeals.find((row) => row.id === deal.id);
        return initial != null && hasKilledDealRowEdits(deal, initial);
      }),
    [killedDeals, initialDeals],
  );

  const handleCancel = useCallback(() => {
    setDeals(structuredClone(initialDeals));
    setEditMode(false);
  }, [initialDeals]);

  const handleSave = useCallback(async () => {
    if (!dirtyGrid) {
      setEditMode(false);
      return;
    }

    const changedDeals = killedDeals.filter((deal) => {
      const initial = initialDeals.find((row) => row.id === deal.id);
      return initial != null && hasKilledDealRowEdits(deal, initial);
    });

    if (changedDeals.length === 0) {
      setEditMode(false);
      return;
    }

    setSaving(true);
    try {
      for (const deal of changedDeals) {
        if (deal.id == null || typeof deal.id !== "number") continue;

        const response = await apiPut(
          "deal",
          deal.id,
          prepareDealPutPayload(deal, {
            kill_reason: deal.kill_reason ?? null,
          }),
        );

        if (!response.success) {
          showErrorFromResponse(response);
          return;
        }
      }

      setInitialDeals(structuredClone(deals));
      setEditMode(false);
    } catch {
      showErrorFromResponse({
        success: false,
        status: 500,
        message: "Failed to save kill reasons. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  }, [deals, dirtyGrid, initialDeals, killedDeals, showErrorFromResponse]);

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSave);

  const handleExportToExcel = useCallback(() => {
    setIsExporting(true);
    try {
      exportKilledDealsToExcel({
        deals: filteredDeals,
        missingOnly,
      });
    } catch (error) {
      showErrorFromResponse({
        success: false,
        status: 500,
        message:
          error instanceof Error
            ? error.message
            : "Failed to export killed deals. Please try again.",
      });
    } finally {
      setIsExporting(false);
    }
  }, [filteredDeals, missingOnly, showErrorFromResponse]);

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<Deal>) => {
      if (!editMode) return;

      const { colDef, newValue, oldValue, data } = event;
      const field = colDef.field;
      if (!data?.id || !field) return;

      if (isGridCellValueEqual(newValue, oldValue)) return;

      const committed = data[field as keyof Deal];
      setDeals((prevDeals) =>
        prevDeals.map((deal) =>
          deal.id === data.id ? { ...deal, [field]: committed } : deal,
        ),
      );
    },
    [editMode],
  );

  const columnDefs = useMemo((): ColDef<Deal>[] => {
    const pipelineCols: ColDef<Deal>[] = buildPipelineColumnDefs({
      editMode,
      lists: {
        condensedSponsors,
        sources,
        countries,
        sectors,
        status,
        users,
        loanTypes,
        riskClasses,
        securityTypes,
        transactionTypes,
        currencies,
        killReasons: reasons,
        capitalSources,
      },
    });

    return pipelineCols
      .filter(
        (col) =>
          typeof col.field === "string" && KILLED_DEALS_COLUMNS.has(col.field),
      )
      .map((col) =>
        col.field === "kill_reason"
          ? { ...col, editable: editMode }
          : { ...col, editable: false },
      );
  }, [
    condensedSponsors,
    sources,
    countries,
    sectors,
    status,
    users,
    loanTypes,
    riskClasses,
    securityTypes,
    transactionTypes,
    currencies,
    capitalSources,
    reasons,
    editMode,
  ]);

  if (isLoading) {
    return (
      <Box
        sx={{
          padding: "20px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <CircularProgress size={18} />
        <Typography sx={{ fontSize: "14px", color: "#182737" }}>
          Loading killed deals…
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mt: 2,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={missingOnly}
                onChange={(_, checked) => setMissingOnly(checked)}
                size="small"
                disabled={editMode}
              />
            }
            label="Missing kill reason only"
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "13px",
                color: "#182737",
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
              minHeight: 32,
            }}
          >
            {editMode ? (
              <>
                <CustomButton
                  text="Cancel"
                  handleButtonPress={handleCancel}
                  isButtonDisabled={saving}
                  solid={false}
                  widthOverride="100px"
                />
                <CustomButton
                  text={saving ? "Saving…" : "Save"}
                  handleButtonPress={requestSave}
                  isButtonDisabled={saving || (!dirtyGrid && !isCellEditing)}
                  solid
                  widthOverride="100px"
                />
              </>
            ) : (
              <>
                <Box
                  sx={{
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    "& .MuiIconButton-root": { width: 32, height: 32 },
                  }}
                >
                  <CustomIconButton
                    icon={
                      <Box
                        component="img"
                        src={pencilIcon}
                        alt="Edit killed deals"
                        sx={{ width: 20, height: 20, display: "block" }}
                      />
                    }
                    onClick={() => {
                      if (!canEdit) return;
                      setEditMode(true);
                    }}
                    disabled={!canEdit || filteredDeals.length === 0}
                    tooltip="Edit"
                  />
                </Box>
                <CustomButton
                  text="Export to Excel"
                  handleButtonPress={handleExportToExcel}
                  isButtonDisabled={isExporting || filteredDeals.length === 0}
                  isLoading={isExporting}
                  isLoadingText="Exporting…"
                  solid={false}
                  widthOverride="140px"
                />
              </>
            )}
          </Box>
        </Box>

        <Typography sx={{ fontSize: "13px", color: "#182737" }}>
          {missingKillReasonCount === 0 ? (
            <>All killed deals have a kill reason recorded.</>
          ) : (
            <>
              <strong>{missingKillReasonCount}</strong>{" "}
              {missingKillReasonCount === 1 ? "deal" : "deals"} missing a kill
              reason
              {editMode ? " — edit Kill Reason, then Save." : "."}
            </>
          )}
          {saving ? " Saving…" : ""}
          {!missingOnly ? (
            <>
              {" "}
              · Showing <strong>{filteredDeals.length}</strong> killed deals
            </>
          ) : null}
        </Typography>

        <Box sx={{ width: "100%", height: 650 }}>
          {filteredDeals.length === 0 ? (
            <Typography sx={{ fontSize: "14px", color: "#182737", py: 2 }}>
              {missingOnly
                ? "No killed deals are missing a kill reason."
                : "No killed deals found."}
            </Typography>
          ) : (
            <div
              className="ag-theme-alpine pipeline-deals-grid"
              style={{ width: "100%", height: "100%" }}
            >
              <AgGridReact<Deal>
                {...gridEditProps}
                rowData={filteredDeals}
                columnDefs={columnDefs}
                onCellValueChanged={onCellValueChanged}
                rowHeight={27}
                singleClickEdit={true}
                getRowId={({ data }) => data?.id?.toString() ?? ""}
              />
            </div>
          )}
        </Box>
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </>
  );
}
